import { useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ReportHeader } from "./components/ReportHeader";
import { DocumentMetaPanel } from "./components/DocumentMetaPanel";
import { EditorFileBar } from "./components/EditorFileBar";
import { EditorStatusBar } from "./components/EditorStatusBar";
import { FindReplacePanel } from "./components/FindReplacePanel";
import { PreviewTOC } from "./components/PreviewTOC";
import { usePersistedDocument } from "./hooks/usePersistedDocument";
import { useInsertAtCursor } from "./hooks/useInsertAtCursor";
import { markdownComponents } from "./markdown/markdownComponents";
import { documentRehypePlugins } from "./markdown/rehypeDocument";
import "github-markdown-css/github-markdown-light.css";
import "./mdwriter.css";

const PRINT_PAGE_STYLE_ID = "vizhacks-print-page-numbers-css";
const IMAGE_PASTE_MAX_BYTES = 1.5 * 1024 * 1024;

function formatSavedTime(ts, language) {
  const loc = language?.startsWith("ar") ? "ar" : "en";
  try {
    return new Intl.DateTimeFormat(loc, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(new Date(ts));
  } catch {
    return new Date(ts).toLocaleTimeString(loc);
  }
}

export default function MdWriterScreen() {
  const { t, i18n } = useTranslation();
  /** Match site locale so Arabic preview/editor aren’t stuck LTR by `dir="auto"` + GitHub CSS. */
  const contentDir = i18n.language?.startsWith("ar") ? "rtl" : "ltr";
  const {
    markdown,
    setMarkdown,
    showLetterhead,
    setShowLetterhead,
    showPrintPageNumbers,
    setShowPrintPageNumbers,
    spellCheck,
    setSpellCheck,
    meta,
    setMeta,
    clearDraft,
    lastSavedAt,
  } = usePersistedDocument();

  const [focusMode, setFocusMode] = useState("split");

  const textareaRef = useRef(null);
  const previewScrollRef = useRef(null);

  const insertAtCursor = useInsertAtCursor(
    markdown,
    setMarkdown,
    textareaRef,
  );

  const insertImageFromFile = useCallback(
    (file) => {
      if (!file.type.startsWith("image/")) return;
      if (file.size > IMAGE_PASTE_MAX_BYTES) {
        window.alert(
          t("mdwriter.imageTooLarge", {
            mb: Math.round(IMAGE_PASTE_MAX_BYTES / 1024 / 1024),
          }),
        );
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = typeof reader.result === "string" ? reader.result : "";
        if (!dataUrl) return;
        const alt =
          file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ") ||
          t("mdwriter.imageAltFallback");
        insertAtCursor(`\n\n![${alt}](${dataUrl})\n\n`);
      };
      reader.readAsDataURL(file);
    },
    [insertAtCursor, t],
  );

  const onPaste = useCallback(
    (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            e.preventDefault();
            insertImageFromFile(file);
          }
          return;
        }
      }
    },
    [insertImageFromFile],
  );

  const runPrint = useCallback(() => {
    window.print();
  }, []);

  /** Lets print CSS hide site chrome (nav, footer) and show only preview. */
  useEffect(() => {
    document.documentElement.classList.add("mdwriter-print-context");
    return () => {
      document.documentElement.classList.remove("mdwriter-print-context");
    };
  }, []);

  useEffect(() => {
    const existing = document.getElementById(PRINT_PAGE_STYLE_ID);
    if (existing) existing.remove();

    if (!showPrintPageNumbers) return;

    const style = document.createElement("style");
    style.id = PRINT_PAGE_STYLE_ID;
    style.textContent = `
@media print {
  @page {
    margin: 14mm 12mm 22mm 12mm;
    @bottom-center {
      content: counter(page);
      font-family: ui-sans-serif, system-ui, -apple-system, sans-serif;
      font-size: 9pt;
      color: #57606a;
    }
  }
}
`;
    document.head.appendChild(style);
    return () => {
      document.getElementById(PRINT_PAGE_STYLE_ID)?.remove();
    };
  }, [showPrintPageNumbers]);

  const rootClass =
    "mdwriter-root app-root" +
    (focusMode === "editor" ? " app-root--focus-editor" : "") +
    (focusMode === "preview" ? " app-root--focus-preview" : "");

  return (
    <div className={rootClass}>
      <section
        className="editor-column no-print"
        aria-label={t("mdwriter.editorAria")}
        hidden={focusMode === "preview" ? true : undefined}
      >
        <header className="editor-toolbar">
          <div className="editor-brand">
            <h1 className="editor-brand-title">
              {t("mdwriter.brandTitle")}{" "}
              <span className="editor-brand-mark">{t("mdwriter.brandMark")}</span>
            </h1>
            <p className="editor-brand-tagline">{t("mdwriter.tagline")}</p>
          </div>
          <div className="editor-toolbar-actions">
            <button type="button" className="btn-print" onClick={runPrint}>
              {t("mdwriter.printPdf")}
            </button>
          </div>
        </header>

        {focusMode === "editor" ? (
          <div
            className="editor-focus-bar no-print"
            role="region"
            aria-label={t("mdwriter.previewHiddenAria")}
          >
            <p className="editor-focus-bar__hint">
              <Trans
                i18nKey="mdwriter.previewHiddenHint"
                components={{ strong: <strong /> }}
              />
            </p>
            <button
              type="button"
              className="btn-focus-escape"
              onClick={() => setFocusMode("split")}
            >
              {t("mdwriter.showPreviewSplit")}
            </button>
          </div>
        ) : null}

        <EditorFileBar
          meta={meta}
          markdown={markdown}
          setMarkdown={setMarkdown}
          insertAtCursor={insertAtCursor}
          onClearDraft={clearDraft}
          focusMode={focusMode}
          setFocusMode={setFocusMode}
        />

        <div
          className="editor-options"
          role="group"
          aria-label={t("mdwriter.optionsGroupAria")}
        >
          <label className="editor-toggle">
            <span className="editor-toggle-check-wrap">
              <input
                type="checkbox"
                className="editor-toggle-input"
                checked={showLetterhead}
                onChange={(e) => setShowLetterhead(e.target.checked)}
              />
              <span className="editor-toggle-indicator" aria-hidden="true" />
            </span>
            <span className="editor-toggle-label">{t("mdwriter.letterhead")}</span>
            <span className="editor-toggle-hint">{t("mdwriter.letterheadHint")}</span>
          </label>
          <label className="editor-toggle">
            <span className="editor-toggle-check-wrap">
              <input
                type="checkbox"
                className="editor-toggle-input"
                checked={showPrintPageNumbers}
                onChange={(e) => setShowPrintPageNumbers(e.target.checked)}
              />
              <span className="editor-toggle-indicator" aria-hidden="true" />
            </span>
            <span className="editor-toggle-label">{t("mdwriter.pageNumbers")}</span>
            <span className="editor-toggle-hint">{t("mdwriter.pageNumbersHint")}</span>
          </label>
          <label className="editor-toggle">
            <span className="editor-toggle-check-wrap">
              <input
                type="checkbox"
                className="editor-toggle-input"
                checked={spellCheck}
                onChange={(e) => setSpellCheck(e.target.checked)}
              />
              <span className="editor-toggle-indicator" aria-hidden="true" />
            </span>
            <span className="editor-toggle-label">{t("mdwriter.spellCheck")}</span>
            <span className="editor-toggle-hint">{t("mdwriter.spellCheckHint")}</span>
          </label>
        </div>

        <DocumentMetaPanel
          meta={meta}
          setMeta={setMeta}
          markdown={markdown}
          setMarkdown={setMarkdown}
        />

        <FindReplacePanel
          textareaRef={textareaRef}
          markdown={markdown}
          setMarkdown={setMarkdown}
        />

        <div className="editor-textarea-wrap">
          <textarea
            ref={textareaRef}
            className="editor-textarea"
            dir={contentDir}
            lang={i18n.language?.startsWith("ar") ? "ar" : "en"}
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            onPaste={onPaste}
            spellCheck={spellCheck}
            placeholder={t("mdwriter.textareaPlaceholder")}
            aria-label={t("mdwriter.textareaAria")}
          />
          <EditorStatusBar text={markdown} />
          {lastSavedAt != null ? (
            <p className="editor-autosave-hint" role="status">
              <span className="editor-autosave-hint__label">
                {t("mdwriter.draftSavedPrefix")}
              </span>{" "}
              <time dateTime={new Date(lastSavedAt).toISOString()}>
                {formatSavedTime(lastSavedAt, i18n.language)}
              </time>
            </p>
          ) : (
            <p className="editor-autosave-hint" role="status">
              {t("mdwriter.draftAutosave")}
            </p>
          )}
        </div>
      </section>

      <main
        id="output"
        className="preview-pane"
        aria-label={t("mdwriter.previewAria")}
        hidden={focusMode === "editor" ? true : undefined}
      >
        <div className="preview-label no-print">{t("mdwriter.previewLabel")}</div>
        {focusMode === "preview" ? (
          <div
            className="preview-focus-bar no-print"
            role="toolbar"
            aria-label={t("mdwriter.previewToolbarAria")}
          >
            <button
              type="button"
              className="btn-focus-escape"
              onClick={() => setFocusMode("split")}
            >
              {t("mdwriter.showEditorSplit")}
            </button>
            <label className="preview-focus-bar__layout">
              <span className="preview-focus-bar__layout-label">{t("mdwriter.view")}</span>
              <select
                className="editor-select editor-select--narrow"
                value={focusMode}
                onChange={(e) => setFocusMode(e.target.value)}
                aria-label={t("mdwriter.layoutAria")}
              >
                <option value="split">{t("mdwriter.layoutSplit")}</option>
                <option value="editor">{t("mdwriter.layoutEditor")}</option>
                <option value="preview">{t("mdwriter.layoutPreview")}</option>
              </select>
            </label>
            <button
              type="button"
              className="btn-focus-escape btn-focus-escape--secondary"
              onClick={runPrint}
            >
              {t("mdwriter.printPdf")}
            </button>
          </div>
        ) : null}
        <div className="preview-scroll" ref={previewScrollRef}>
          <PreviewTOC markdown={markdown} scrollRootRef={previewScrollRef} />
          <div className="markdown-body" dir={contentDir} lang={i18n.language?.startsWith("ar") ? "ar" : "en"}>
            {showLetterhead ? (
              <>
                <ReportHeader meta={meta} />
                <hr className="report-header-divider" />
              </>
            ) : null}
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={documentRehypePlugins}
              components={markdownComponents}
            >
              {markdown}
            </ReactMarkdown>
          </div>
        </div>
      </main>
    </div>
  );
}
